import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Send, UserX, Shield, Edit3, Ban, Trash2, Crown, Users, X, Check, ArrowLeft, Menu
} from "lucide-react";

export default function GroupDetail() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const chatEndRef = useRef(null);

  const loadGroup = async () => {
    const res = await api.get(`/groups/${groupId}`);
    setGroup(res.data);
  };

  const loadMessages = async () => {
    const res = await api.get(`/groups/${groupId}/messages`);
    setMessages(res.data || []);
  };

  useEffect(() => {
    loadGroup().then(loadMessages).finally(() => setLoading(false));
    const interval = setInterval(loadMessages, 2000);
    return () => clearInterval(interval);
  }, [groupId]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Helper to get member's profile image
  const getMemberImage = (senderId) => {
    const member = group?.members?.find(m => m.user_id === senderId);
    return member?.profile_image || null;
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    const replyTo = replyingTo;
    setReplyingTo(null);
    const tempId = `temp_${Date.now()}`;
    const optimisticMsg = {
      message_id: tempId, sender_id: user.user_id, sender_name: "You",
      content: input, created_at: new Date().toISOString(),
      reply_to: replyTo ? { sender_name: replyTo.sender_name, content: replyTo.content } : null,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setInput("");
    try {
      const payload = { content: input };
      if (replyTo) {
        payload.reply_to_id = replyTo.message_id;
        payload.reply_to_content = replyTo.content;
        payload.reply_to_sender = replyTo.sender_name;
      }
      await api.post(`/groups/${groupId}/messages`, payload);
      loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/groups/${groupId}/messages/${messageId}`);
    loadMessages();
  };

  const handleBan = async (targetId) => {
    const reason = prompt("Reason for ban?");
    if (!reason) return;
    await api.post(`/groups/${groupId}/bans`, { user_id: targetId, reason });
    loadGroup(); loadMessages();
    toast.success("User banned");
  };

  const handleRoleChange = async (targetId, newRole) => {
    await api.put(`/groups/${groupId}/members/${targetId}/role`, { role: newRole });
    loadGroup();
    toast.success("Role updated");
  };

  const handleRemoveMember = async (targetId) => {
    if (!window.confirm("Remove this member?")) return;
    await api.delete(`/groups/${groupId}/members/${targetId}`);
    loadGroup();
    toast.success("Member removed");
  };

  const startReply = (msg) => setReplyingTo(msg);
  const cancelReply = () => setReplyingTo(null);

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, "0");
    const mins = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${mins}`;
  };

  if (loading) return <div className="min-h-screen"><Navbar /><div className="p-8 text-center">Loading...</div></div>;
  if (!group) return <div className="min-h-screen"><Navbar /><div className="p-8 text-center">Group not found.</div></div>;

  const myRole = group.my_role;
  const isCreator = myRole === "creator";
  const isModerator = myRole === "moderator" || isCreator;

  return (
    <div className="min-h-screen bg-[#EFEAE2]">
      <Navbar />
      <main className="max-w-2xl mx-auto h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="bg-[#F0F2F5] border-b px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <Link to="/groups" className="p-1 hover:bg-gray-200 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="w-10 h-10 rounded-full bg-[#005C4B]/10 flex items-center justify-center text-lg">
            <Users className="w-5 h-5 text-[#005C4B]" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-sm text-gray-900">{group.title}</h2>
            <p className="text-xs text-gray-500">{group.members?.length || 0} members</p>
          </div>
          {isCreator && (
            <Link to={`/groups/${groupId}/edit`} className="p-2 hover:bg-gray-200 rounded-lg">
              <Edit3 className="w-4 h-4 text-gray-500" />
            </Link>
          )}
          <button onClick={() => setShowMembers(!showMembers)} className="p-2 hover:bg-gray-200 rounded-lg">
            <Menu className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1"
          style={{
            backgroundImage: "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAOxAAADsQBlSsOGwAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAAAWdEVYdFRpdGxlAEJlYWNvbi5qcGVnQ1JFV0EAAAKsSURBVGiB7ZpNjtQwEEa/4gTsOUJvOEJL4gJIiAuw5QhICFbsWMI1eo4GCdHdLjtJ2amfqspf8j5p1Op2+XPZVU4nKVlZeZKkOyLiKiI+IuI2Ij4j4nO59j9K1j7LelzW5+8REWtE/FnLD7oHWDtrr8AJcDa5Tw/gj61qEwhsflK9v0TEr2WMfx7gI1X7+q4JTG/eAx4YcJvALIHpvUZEO0dawIAXX8AD4G0CC4DXSZnNA74D3MqvteCcIQ7MUzScC3JDHwHuD+B9JqwBfKMxluMALnrA+0w4MRbGcfQwBnj2BHBfB3xPwtqLfMw81tLyGBCdAW+JcDKhvYX07C5yP0/9FuvB1QSYkwlrI9BYTqjGJmGgKox7gB8RtXACsN6BngMME4BXmq8C/BmTi/wA/FyLtjEDcPx7x74E8GHyHlMP8Gj8jR7gLOLfFWCdk3gNuPzJQVa/wt1rsc8M0O3b1gSQTqMmz9QO03dk8YQlMl+R/jXLBFss9tFrwCev78xzGpv5crtQ6++zTP+O0s1t7oGL/jkP9OmfgF5u1S7ujC+QLx4bICJE4P3bIwFfvGVdGeLDM54C7t/PbwgAPuRMNYUx9xR1QdSsyCk8FgzC4wD3zPAVOORkzty2AM6BdY6BzM84oow0S3mAC8BJcTTjTwfwGfBm3KoCbsazHeBTD7ifP93iHSDdby8O8K4PeIAcnbuqAY4C4wFf2IAXDa5rnhp5nYHh+fOBTX9sg80iS1d1zxK7M1r8H9m96PJd7W2lBD5OudmlJdAc1+oLk/bNyOXrLNo4EONIvgCu83I0OpxMboGDJvXH2sUGNwuTBNzO8Vqz+4K/AYynffDA3GlvAAAAAElFTkSuQmCC')",
            backgroundColor: "#EFEAE2",
          }}
        >
          {messages.map(msg => {
            const memberImage = getMemberImage(msg.sender_id);
            return (
              <div key={msg.message_id}>
                <div className={`flex ${msg.sender_id === user.user_id ? "justify-end" : "justify-start"} items-end gap-1`}>
                  {msg.sender_id !== user.user_id && (
                    <div className="w-7 h-7 rounded-full overflow-hidden bg-gray-300 flex-shrink-0 mb-0.5">
                      {memberImage ? (
                        <img src={memberImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-white">
                          {(msg.sender_name || "?")[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="max-w-[75%] group">
                    {msg.reply_to && (
                      <div className={`mb-0.5 px-2 py-1 rounded-t-lg text-[11px] ${msg.sender_id === user.user_id ? "bg-[#025144]/40 text-white/90 ml-auto" : "bg-gray-200/80 text-gray-600"} max-w-[90%]`}>
                        <span className="font-medium">{msg.reply_to.sender_name}</span>
                        <p className="truncate opacity-80">{msg.reply_to.content}</p>
                      </div>
                    )}
                    <div className="relative">
                      {msg.sender_id !== user.user_id && (
                        <div className="absolute top-0 -left-[6px] w-2 h-2 bg-white" style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }} />
                      )}
                      {msg.sender_id === user.user_id && (
                        <div className="absolute bottom-0 -right-[6px] w-2 h-2 bg-[#005C4B]" style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }} />
                      )}
                      <div className={`relative ${msg.sender_id === user.user_id ? "bg-[#005C4B] text-white rounded-lg rounded-br-sm" : "bg-white text-gray-900 rounded-lg rounded-tl-sm"} shadow-sm`}>
                        {msg.sender_id !== user.user_id && (
                          <p className="px-2.5 pt-1.5 pb-0 text-[11px] font-semibold text-[#005C4B]">{msg.sender_name}</p>
                        )}
                        <div className={`${msg.sender_id !== user.user_id ? "px-2.5 pb-1.5 pt-0" : "px-2.5 py-1.5"} pr-16`}>
                          <p className="text-[14px] leading-[1.4] whitespace-pre-wrap break-words">
                            {msg.deleted ? <i className="opacity-50">Message deleted</i> : msg.content}
                          </p>
                        </div>
                        <div className={`absolute bottom-0.5 right-2 flex items-center gap-1 text-[10px] ${msg.sender_id === user.user_id ? "text-white/55" : "text-gray-400"}`}>
                          <button onClick={() => startReply(msg)} className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-white mr-0.5">↩</button>
                          {isModerator && !msg.deleted && msg.sender_id !== user.user_id && (
                            <button onClick={() => handleDeleteMessage(msg.message_id)} className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-300 mr-0.5">🗑</button>
                          )}
                          <span>{formatTime(msg.created_at)}</span>
                          {msg.sender_id === user.user_id && <Check className="w-3 h-3" />}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>

        {/* Reply bar */}
        {replyingTo && (
          <div className="px-4 py-2 bg-[#F0F2F5] border-t flex items-center gap-3 flex-shrink-0">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#005C4B]">Replying to {replyingTo.sender_name}</p>
              <p className="text-xs text-gray-500 truncate">{replyingTo.content}</p>
            </div>
            <button onClick={cancelReply} className="p-1 hover:bg-gray-200 rounded"><X className="w-4 h-4 text-gray-500" /></button>
          </div>
        )}

        {/* Input */}
        <div className="bg-[#F0F2F5] px-3 py-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSend()}
              className="flex-1 px-4 py-2.5 bg-white rounded-full text-sm outline-none border-none"
              placeholder={replyingTo ? "Reply..." : "Type a message..."} />
            <button onClick={handleSend} className="w-10 h-10 rounded-full bg-[#005C4B] text-white flex items-center justify-center">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>

      {/* Members slide-out panel */}
      {showMembers && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMembers(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <Users className="w-4 h-4" /> Members ({group.members?.length || 0})
              </h3>
              <button onClick={() => setShowMembers(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {group.members?.map(m => (
                <div key={m.user_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* Profile image */}
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                      {m.profile_image ? (
                        <img src={m.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[11px] font-bold text-gray-500">
                          {(m.display_name || "?")[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-gray-700">{m.display_name}</span>
                    {m.role === "creator" && <Crown className="w-3 h-3 text-yellow-500" />}
                    {m.role === "moderator" && <Shield className="w-3 h-3 text-blue-500" />}
                  </div>
                  {isCreator && m.user_id !== user.user_id && (
                    <div className="flex gap-1">
                      <select value={m.role} onChange={(e) => handleRoleChange(m.user_id, e.target.value)} className="text-[10px] border rounded px-1 py-0.5">
                        <option value="member">Member</option>
                        <option value="moderator">Mod</option>
                      </select>
                      <button onClick={() => handleRemoveMember(m.user_id)} className="text-red-500"><UserX className="w-3 h-3" /></button>
                      <button onClick={() => handleBan(m.user_id)} className="text-red-700"><Ban className="w-3 h-3" /></button>
                    </div>
                  )}
                  {isModerator && !isCreator && m.role === "member" && (
                    <button onClick={() => handleRemoveMember(m.user_id)} className="text-red-500"><UserX className="w-3 h-3" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}